/**
 * Enhanced Product Gallery with Advanced Zoom & Lightbox
 * Features:
 * - Pinch-to-zoom on mobile
 * - Better touch gestures (swipe, double-tap)
 * - Improved lightbox with zoom controls
 * - Keyboard navigation
 * - Smooth animations
 */

if (!customElements.get('enhanced-product-gallery')) {
  customElements.define(
    'enhanced-product-gallery',
    class EnhancedProductGallery extends HTMLElement {
      constructor() {
        super();
        this.currentZoom = 1;
        this.maxZoom = 3;
        this.minZoom = 1;
        this.zoomStep = 0.5;
        this.isDragging = false;
        this.startX = 0;
        this.startY = 0;
        this.translateX = 0;
        this.translateY = 0;

        // Touch gesture tracking
        this.touchStartDistance = 0;
        this.touchStartZoom = 1;
        this.lastTap = 0;

        this.init();
      }

      init() {
        this.setupImageEnhancements();
        this.setupKeyboardControls();
        this.setupTouchGestures();
      }

      setupImageEnhancements() {
        const images = this.querySelectorAll('.product__media img');

        images.forEach(img => {
          // Add zoom on hover for desktop
          if (window.matchMedia('(min-width: 750px)').matches) {
            img.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
            img.addEventListener('mousemove', this.handleMouseMove.bind(this));
            img.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
          }

          // Add double-tap zoom for mobile
          img.addEventListener('touchend', this.handleDoubleTap.bind(this));
        });
      }

      setupTouchGestures() {
        const container = this.querySelector('[role="document"]') || this;

        // Pinch to zoom
        container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
        container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
        container.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
      }

      handleTouchStart(e) {
        if (e.touches.length === 2) {
          e.preventDefault();
          this.touchStartDistance = this.getDistance(e.touches[0], e.touches[1]);
          this.touchStartZoom = this.currentZoom;
        } else if (e.touches.length === 1) {
          this.startX = e.touches[0].clientX - this.translateX;
          this.startY = e.touches[0].clientY - this.translateY;
          this.isDragging = this.currentZoom > 1;
        }
      }

      handleTouchMove(e) {
        if (e.touches.length === 2) {
          e.preventDefault();
          const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
          const scale = currentDistance / this.touchStartDistance;
          const newZoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.touchStartZoom * scale));
          this.applyZoom(newZoom, e.target);
        } else if (e.touches.length === 1 && this.isDragging) {
          e.preventDefault();
          const x = e.touches[0].clientX - this.startX;
          const y = e.touches[0].clientY - this.startY;
          this.applyTransform(e.target, x, y);
        }
      }

      handleTouchEnd(e) {
        this.isDragging = false;
      }

      handleDoubleTap(e) {
        const now = Date.now();
        const timeSinceLastTap = now - this.lastTap;

        if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
          e.preventDefault();

          if (this.currentZoom === 1) {
            this.zoomIn(e.target);
          } else {
            this.resetZoom(e.target);
          }
        }

        this.lastTap = now;
      }

      getDistance(touch1, touch2) {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      setupKeyboardControls() {
        document.addEventListener('keydown', (e) => {
          if (!this.isModalOpen()) return;

          const activeImage = this.querySelector('.active img');
          if (!activeImage) return;

          switch(e.key) {
            case '+':
            case '=':
              e.preventDefault();
              this.zoomIn(activeImage);
              break;
            case '-':
            case '_':
              e.preventDefault();
              this.zoomOut(activeImage);
              break;
            case '0':
              e.preventDefault();
              this.resetZoom(activeImage);
              break;
            case 'ArrowLeft':
              if (this.currentZoom === 1) {
                this.navigatePrevious();
              } else {
                e.preventDefault();
                this.pan(activeImage, 50, 0);
              }
              break;
            case 'ArrowRight':
              if (this.currentZoom === 1) {
                this.navigateNext();
              } else {
                e.preventDefault();
                this.pan(activeImage, -50, 0);
              }
              break;
            case 'ArrowUp':
              e.preventDefault();
              this.pan(activeImage, 0, 50);
              break;
            case 'ArrowDown':
              e.preventDefault();
              this.pan(activeImage, 0, -50);
              break;
          }
        });
      }

      handleMouseEnter(e) {
        const img = e.target;
        img.style.cursor = 'zoom-in';
      }

      handleMouseMove(e) {
        const img = e.target;
        if (this.currentZoom <= 1) return;

        const rect = img.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        img.style.transformOrigin = `${x}% ${y}%`;
      }

      handleMouseLeave(e) {
        const img = e.target;
        if (this.currentZoom <= 1) {
          img.style.cursor = 'zoom-in';
          img.style.transform = 'scale(1)';
        }
      }

      zoomIn(target) {
        this.currentZoom = Math.min(this.maxZoom, this.currentZoom + this.zoomStep);
        this.applyZoom(this.currentZoom, target);
      }

      zoomOut(target) {
        this.currentZoom = Math.max(this.minZoom, this.currentZoom - this.zoomStep);
        this.applyZoom(this.currentZoom, target);

        if (this.currentZoom === 1) {
          this.translateX = 0;
          this.translateY = 0;
        }
      }

      resetZoom(target) {
        this.currentZoom = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.applyZoom(1, target);
        target.style.transform = 'scale(1) translate(0, 0)';
        target.style.cursor = 'zoom-in';
      }

      applyZoom(zoom, target) {
        this.currentZoom = zoom;
        const img = target.closest('img') || target.querySelector('img');
        if (!img) return;

        img.style.transform = `scale(${zoom}) translate(${this.translateX}px, ${this.translateY}px)`;
        img.style.transition = 'transform 0.2s ease-out';
        img.style.cursor = zoom > 1 ? 'grab' : 'zoom-in';

        // Update zoom indicator
        this.updateZoomIndicator(zoom);
      }

      applyTransform(target, x, y) {
        this.translateX = x;
        this.translateY = y;

        const img = target.closest('img') || target.querySelector('img');
        if (!img) return;

        img.style.transform = `scale(${this.currentZoom}) translate(${x}px, ${y}px)`;
        img.style.cursor = 'grabbing';
      }

      pan(target, deltaX, deltaY) {
        this.translateX += deltaX;
        this.translateY += deltaY;
        this.applyTransform(target, this.translateX, this.translateY);
      }

      updateZoomIndicator(zoom) {
        let indicator = this.querySelector('.zoom-indicator');

        if (!indicator) {
          indicator = document.createElement('div');
          indicator.className = 'zoom-indicator';
          indicator.setAttribute('aria-live', 'polite');
          indicator.setAttribute('aria-atomic', 'true');
          this.appendChild(indicator);
        }

        const percentage = Math.round(zoom * 100);
        indicator.textContent = `${percentage}%`;
        indicator.style.opacity = '1';

        clearTimeout(this.indicatorTimeout);
        this.indicatorTimeout = setTimeout(() => {
          indicator.style.opacity = '0';
        }, 1500);
      }

      isModalOpen() {
        const modal = document.querySelector('product-modal');
        return modal && modal.classList.contains('modal--opened');
      }

      navigatePrevious() {
        const prevButton = this.querySelector('.slider-button--prev');
        if (prevButton) prevButton.click();
      }

      navigateNext() {
        const nextButton = this.querySelector('.slider-button--next');
        if (nextButton) nextButton.click();
      }
    }
  );
}

// Enhanced modal opener with better touch support
if (!customElements.get('enhanced-modal-opener')) {
  customElements.define(
    'enhanced-modal-opener',
    class EnhancedModalOpener extends HTMLElement {
      constructor() {
        super();
        this.setupEnhancedOpener();
      }

      setupEnhancedOpener() {
        const button = this.querySelector('button');
        if (!button) return;

        // Add touch support
        button.addEventListener('touchend', (e) => {
          e.preventDefault();
          button.click();
        }, { passive: false });

        // Add loading state
        button.addEventListener('click', () => {
          this.classList.add('loading');
        });
      }
    }
  );
}

// Add zoom controls to modal
document.addEventListener('DOMContentLoaded', () => {
  const modal = document.querySelector('product-modal');
  if (!modal) return;

  // Create zoom controls
  const controls = document.createElement('div');
  controls.className = 'product-modal-zoom-controls';
  controls.innerHTML = `
    <button type="button" class="zoom-control zoom-in" aria-label="Zoom in" title="Zoom in (+)">
      <svg viewBox="0 0 20 20" width="20" height="20">
        <path d="M10 4v12M4 10h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
    <button type="button" class="zoom-control zoom-out" aria-label="Zoom out" title="Zoom out (-)">
      <svg viewBox="0 0 20 20" width="20" height="20">
        <path d="M4 10h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
    <button type="button" class="zoom-control zoom-reset" aria-label="Reset zoom" title="Reset zoom (0)">
      <svg viewBox="0 0 20 20" width="20" height="20">
        <path d="M10 2a8 8 0 100 16 8 8 0 000-16z" stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M6 10h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
  `;

  modal.querySelector('.product-media-modal__dialog')?.appendChild(controls);

  // Add event listeners
  const gallery = document.querySelector('enhanced-product-gallery, media-gallery');
  if (!gallery) return;

  controls.querySelector('.zoom-in')?.addEventListener('click', () => {
    const activeImg = modal.querySelector('.active img');
    if (activeImg && gallery.zoomIn) gallery.zoomIn(activeImg);
  });

  controls.querySelector('.zoom-out')?.addEventListener('click', () => {
    const activeImg = modal.querySelector('.active img');
    if (activeImg && gallery.zoomOut) gallery.zoomOut(activeImg);
  });

  controls.querySelector('.zoom-reset')?.addEventListener('click', () => {
    const activeImg = modal.querySelector('.active img');
    if (activeImg && gallery.resetZoom) gallery.resetZoom(activeImg);
  });
});
